import React from 'react';
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import { Field } from 'redux-form';
import { renderTextField } from 'components/form/field/redux-field';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutocompleteMinigrid from 'components/form/field/render-minigrid-autocomplete';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import { ReactSelect } from 'components/form/field/react_select';
import { fetchUserData } from 'services/utils/therapy-service';
import { makeStyles } from '@mui/styles';
import { RolesEnum, ClinicAssignmentsDynamicFieldsEnum } from '../../constants';
import { cpStyle } from '../../index.styles';
import { IFields } from '../../table/types';

interface ISubFormProps {
  formTitle: string;
  fieldsInfo: IFields[];
  caPharmacistListRef: any;
  caLiaisonListRef: any;
  setFieldValue: (field: string, value: any) => void;
  customerId: number;
}

export const ClinicAssigments: React.FC<ISubFormProps> = (props: ISubFormProps): JSX.Element => {
  const useStyles = makeStyles(cpStyle);
  const classes = useStyles();
  const MAX_LENGTH_INPUT = '100';

  const {
    formTitle,
    fieldsInfo,
    caPharmacistListRef,
    caLiaisonListRef,
    setFieldValue,
    customerId,
  } = props;

  const [pharmacistUserList, setPharmacistUserList] = React.useState<
    { label: string; value: number }[]
  >([]);
  const [phramacistsList, setPhramacistsList] = React.useState<{ label: string; value: number }[]>(
    [],
  );
  const [liaisonUserList, setLiaisonUserList] = React.useState<{ label: string; value: number }[]>(
    [],
  );
  const [liaisonList, setLiaisonList] = React.useState<{ label: string; value: number }[]>([]);

  const handleUserList = (searchText: string) =>
    // eslint-disable-next-line no-confusing-arrow
    fetchUserData(searchText).then((result: any) =>
      result.data
        ? result.data.users.map((userData: any) => ({
            id: userData.id,
            label: userData.display_name,
          }))
        : [],
    );

  const handlePharmacistsChange = (pharmacist: { label: string; value: number }[]): void => {
    setPhramacistsList(pharmacist);
  };

  const handleLiaisonsChange = (liaisons: { label: string; value: number }[]): void => {
    setLiaisonList(liaisons);
  };

  React.useEffect(() => {
    caPharmacistListRef.current = phramacistsList;
  }, [phramacistsList]);

  React.useEffect(() => {
    caLiaisonListRef.current = liaisonList;
  }, [liaisonList]);

  React.useEffect(() => {
    const pharmacistUserList = async () => {
      const pharmacistUsers = await ApplicationManagerClient.fetchRoleUsers(
        RolesEnum.PHARMACIST,
        customerId,
      );
      if (pharmacistUsers && pharmacistUsers.data && pharmacistUsers.data.length) {
        setPharmacistUserList(
          pharmacistUsers.data.map(t => {
            return {
              value: t.id,
              label: t.display_name,
            };
          }),
        );
      }
    };
    pharmacistUserList();
  }, []);

  React.useEffect(() => {
    const liaisonUserList = async () => {
      const liaisonUsers = await ApplicationManagerClient.fetchRoleUsers(
        RolesEnum.LIAISON,
        customerId,
      );
      if (liaisonUsers && liaisonUsers.data && liaisonUsers.data.length) {
        setLiaisonUserList(
          liaisonUsers.data.map(t => {
            return {
              value: t.id,
              label: t.display_name,
            };
          }),
        );
      }
    };
    liaisonUserList();
  }, []);

  React.useEffect(() => {
    const tempLoopFields = Object.values(ClinicAssignmentsDynamicFieldsEnum) as string[];
    if (fieldsInfo.length) {
      for (const fieldInfo of fieldsInfo) {
        if (
          fieldInfo.fieldName &&
          tempLoopFields.includes(fieldInfo.fieldName) &&
          Array.isArray(fieldInfo.value) &&
          fieldInfo.value.length
        ) {
          if (
            fieldInfo.fieldName === ClinicAssignmentsDynamicFieldsEnum.CLINIC_ASSIGMENTS_PHARMACISTS
          ) {
            setPhramacistsList(fieldInfo.value);
          }
          if (
            fieldInfo.fieldName === ClinicAssignmentsDynamicFieldsEnum.CLINIC_ASSIGMENTS_LIAISONS
          ) {
            setLiaisonUserList(fieldInfo.value);
          }
        } else if (
          fieldInfo.fieldName &&
          tempLoopFields.includes(fieldInfo.fieldName) &&
          fieldInfo.value !== '' &&
          !Array.isArray(fieldInfo.value)
        ) {
          setFieldValue(fieldInfo.fieldName, fieldInfo.value);
        }
      }
    }
  }, [fieldsInfo]);

  return (
    <Grid item xs={12}>
      <Accordion className={classes.accordionheader}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandIconColor} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>
            <Tooltip title={formTitle} placement="right-end">
              <span className={classes.accordionTitle}>{`${formTitle}`}</span>
            </Tooltip>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.AccordionDetailsForm}>
          <Grid container spacing={3} className={classes.rowMarginTop}>
            <Grid item xs={4}>
              <Field
                name="ca_name"
                label="Name:"
                disabled
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                  value: 'Central Operations',
                }}
              />
            </Grid>
            <Grid item xs={8}>
              <Field
                name="ca_office_locations"
                label="Office Locations:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ReactSelect
                name="ca_pharmacists"
                label="Pharmacists:"
                isMulti
                value={phramacistsList}
                fields={pharmacistUserList}
                handleOnChange={handlePharmacistsChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <ReactSelect
                name="ca_liaisons"
                label="Liaisons:"
                isMulti
                value={liaisonList}
                fields={liaisonUserList}
                handleOnChange={handleLiaisonsChange}
                fullWidth
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
